import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { BetaReaderShell } from '../components/BetaReaderShell';
import { BetaStoryList } from '../components/BetaStoryList';
import { useAdminMode, useUser } from '../providers/UserProvider';
import * as storyRepository from '../repositories/storyRepository';
import type { StoryElement } from '../types';

/**
 * Public, linkable view of one story: the feed's own row rendering, opened.
 * Readable without a session -- the permalink fetch and the row's deferred
 * alternates fetch both go out unauthenticated for anonymous visitors.
 */
export default function StoryPermalinkPage() {
  const { identifier } = useParams<{ identifier: string }>();
  const navigate = useNavigate();
  const { isAdmin, isLoggedIn } = useUser();
  const { adminMode } = useAdminMode();
  const [story, setStory] = useState<StoryElement | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!identifier) return;
    setStory(null);
    setError('');
    storyRepository.getStoryPermalink(identifier)
      .then((result) => {
        setStory(result.story);
        if (result.permalink_id !== identifier) {
          navigate(`/story/${encodeURIComponent(result.permalink_id)}`, { replace: true });
        }
      })
      .catch((reason) => setError((reason as Error).message || 'Story not found'));
  }, [identifier, navigate]);

  return (
    <BetaReaderShell>
      {!story && !error && <p className="beta-message" role="status">Loading story…</p>}
      {error && <p className="beta-message">{error}</p>}
      {story && (
        <BetaStoryList
          eventContext={{ surface: 'article', surfaceId: identifier }}
          articles={[story]}
          resetKey={`permalink:${story.id}`}
          initiallyExpanded
          isAdmin={isAdmin && adminMode}
        />
      )}
      {story && !isLoggedIn && (
        <p className="beta-message">
          Stories like this, picked for you: <Link to="/login">log in</Link> or{' '}
          <Link to="/signup">create a feed</Link>.
        </p>
      )}
    </BetaReaderShell>
  );
}
